/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {LookupService} from '../../../../../common/service/lookup.service';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {MapToolsService} from '../../../../services/map-tools.service';
import {PlanModelService} from '../../../../../common/service/plan-model.service';
import {LineService} from '../../../../services/features/line.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {switchMap} from 'rxjs/operators';
import {AngleUtilService} from '../../../../services/features/angle/angle-util.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {forkJoinDefaultIfEmpty} from '../../../../../common/observable.utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class TotalBoundaryDistanceConstructionToolService implements ToolServiceInterface {
    title = 'TotalBoundaryDistanceConstructionToolService';

    attributes: any;

    private readonly tolerance = 3.6; // 1% of 360

    constructor(
        private angleUtilService: AngleUtilService,
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private lineService: LineService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private mapToolsService: MapToolsService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef
    ) {}

    setupFeatureEventModification() {
        console.debug(`${this.title} - setupFeatureEventModification`);

        this.eventBus
            .on('constructionToolService:totalBoundaryDistance:clear')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                if (meta.data) {
                    this.spatialUtilService.unhighlightFeatures('line');
                }
            });
    }

    create(attributes: any) {
        console.debug(`${this.title} - create`);

        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_TOTAL_LOT_BOUNDARY', () => {
            this.attributes = attributes;

            const tool = this.getTool();

            tool.errors.length = 0;
            tool.warnings.length = 0;
            tool.information.length = 0;

            this.verifySelectedLinesAndCreateBoundaryDistanceLine();
        });
    }

    private getTool() {
        return this.constructionToolModelService.getTool('totalBoundaryDistance');
    }

    private getLineTypeLookups(): LineTypeLookup[] {
        return this.lookupService.getLookups('lineType');
    }

    private getTotalDistance(selectedLines: any[]) {
        let totalDistance = 0.0;
        selectedLines.forEach(line => {
            // DCX-4228: revert TLB calc to 2 decimals to prevent rounding issues
            totalDistance += parseFloat(line.attributes.distance);
        });
        return parseFloat(totalDistance.toFixed(2));
    }

    private createBoundaryDistanceLine(lineAttributes: any, fromPointId: string, toPointId: string, selectedLines: any[]) {
        const points = this.spatialUtilService.getPointGraphics();

        const fromPoint = points.find((p: any) => `[${p.attributes.id}]` === fromPointId);
        const toPoint = points.find((p: any) => `[${p.attributes.id}]` === toPointId);

        lineAttributes.frompointid = fromPoint.attributes.id;
        lineAttributes.topointid = toPoint.attributes.id;

        const lineGraphic = this.createLineGraphic(lineAttributes, fromPoint, toPoint);
        lineGraphic.attributes.guid = this.planModelService.getGuid();

        const subscription = this.lineService
            .create(lineGraphic)
            .pipe(
                switchMap((createdLine: any) => {
                    console.debug('Line has been successfully created!');
                    return forkJoinDefaultIfEmpty(
                        selectedLines.map((line: any) => {
                            line.attributes['totallotboundaryid'] = createdLine[0].attributes.id;
                            return this.lineService.update(line);
                        })
                    );
                })
            )
            .subscribe();
        this.eventBus.cast('nlr:busy', subscription);
    }

    private createLineGraphic(lineAttributes: any, fromPt: any, toPt: any) {
        const lineGeometry = new this.esriModuleService.geometry.Polyline([
            [fromPt.geometry.x, fromPt.geometry.y],
            [toPt.geometry.x, toPt.geometry.y]
        ]);
        lineGeometry.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

        const lineGraphic = new this.esriModuleService.Graphic(lineGeometry);
        lineGraphic.attributes = lineAttributes;

        return lineGraphic;
    }

    private verifySelectedLinesAndCreateBoundaryDistanceLine() {
        const logPrefix = `${this.title} - verifyLines`;
        console.debug(logPrefix);

        const tool = this.getTool();

        const selectedLines = this.spatialUtilService.getHighlightedLines();

        if (selectedLines && selectedLines.length > 1) {
            const pointsFound = [];
            const pointsFoundTwice = [];
            let previousBearing = 0.0;
            let validAngleBetweenLines = true;

            const lotOrRoadLineTypeLookups: LineTypeLookup[] = this.getLineTypeLookups().filter(
                l => l.businessClass === 'Lot' || l.businessClass === 'Road'
            );

            const validLineTypes = selectedLines.every((selectedLine: any) =>
                lotOrRoadLineTypeLookups.some(l => l.id === selectedLine.attributes.type)
            );

            selectedLines.forEach((selectedLine: any) => {
                if (pointsFound.length > 0) {
                    let nextBearing = this.angleUtilService.getLineBearing(selectedLine);

                    if (nextBearing > 180) {
                        nextBearing = nextBearing - 180;
                    }

                    if (
                        Math.abs(previousBearing - nextBearing) >= this.tolerance &&
                        Math.abs(previousBearing - nextBearing) <= 180 - this.tolerance
                    ) {
                        validAngleBetweenLines = false;
                    }
                    previousBearing = nextBearing;
                } else {
                    previousBearing = this.angleUtilService.getLineBearing(selectedLine);

                    if (previousBearing > 180) {
                        previousBearing = previousBearing - 180;
                    }
                }

                const bracketedFromPointId = `[${selectedLine.attributes.frompointid}]`;
                if (pointsFound.includes(bracketedFromPointId) && !pointsFoundTwice.includes(bracketedFromPointId)) {
                    pointsFoundTwice.push('[' + selectedLine.attributes.frompointid + ']');
                }
                pointsFound.push(bracketedFromPointId);

                const bracketedToPointId = `[${selectedLine.attributes.topointid}]`;
                if (pointsFound.includes(bracketedToPointId) && !pointsFoundTwice.includes(bracketedToPointId)) {
                    pointsFoundTwice.push(bracketedToPointId);
                }
                pointsFound.push(bracketedToPointId);
            });

            if (validAngleBetweenLines && pointsFoundTwice.length === selectedLines.length - 1 && validLineTypes) {
                const twoPoints = [];

                pointsFound.forEach((pointFound: string) => {
                    if (!pointsFoundTwice.includes(pointFound)) {
                        twoPoints.push(pointFound);
                    }
                });

                if (twoPoints.length === 2) {
                    console.debug(`${logPrefix} - line end points valid:`, twoPoints);

                    const totalLotBoundaryLineTypeLookup = this.getLineTypeLookups().find(l => l.businessClass === 'TotalLotBoundary');

                    const totalDistance = this.getTotalDistance(selectedLines);

                    const lineIdsString = selectedLines.map((line: any) => `[${line.attributes.id}]`).join();

                    const lineAttributes = {
                        frompointid: undefined,
                        topointid: undefined,
                        type: totalLotBoundaryLineTypeLookup.id,
                        distance: totalDistance,
                        id: undefined,
                        guid: undefined,
                        lineids: lineIdsString,
                        measurementsource: this.attributes.measurementsource?.id || 1
                    };

                    this.createBoundaryDistanceLine(lineAttributes, twoPoints[0], twoPoints[1], selectedLines);
                } else {
                    console.error(`${logPrefix} - line end points invalid:`, twoPoints);
                }
            } else if (!validAngleBetweenLines) {
                console.debug(`${logPrefix} - angles are invalid.`);
                tool.errors.push(tool.errorMessage3);
            } else {
                console.debug(`${logPrefix} - lines are invalid.`);
                tool.errors.push(tool.errorMessage2);
            }
        } else {
            tool.errors.push(tool.errorMessage1);
        }
    }
}
